/**
 * One player in a `World` (docs/ONLINE.md §2): everything that is the player's, not the zone's.
 *
 * The World keeps one slot per player, by entity id. Its old single-player fields (`player`,
 * `progress`, `input`, `auto`, `qinggong` …) are getters on the slot the rules are running for:
 * the primary slot, except while a command from another player is applied (`apply(cmd, from)`)
 * or while that player's tick runs. With one player nothing changes.
 */
import type { Point } from './map';
import type { PlayerProgress } from './progress';
import { newQinggongState, type QinggongState, type QinggongStats } from './qinggong';
import type { PlayerInput } from './systems';
import type { Entity, WorldEvent } from './world';

export interface PlayerSlot {
  /** The player's body (kind 'player'). */
  readonly entity: Entity;
  /** Saved per character. */
  readonly progress: PlayerProgress;
  /** Held controls. */
  readonly input: PlayerInput;
  /** Auto-battle: on/off, the spot it hunts around, and time to the next decision. */
  readonly auto: { on: boolean; ax: number; ay: number; thinkT: number };
  /** The วิชาตัวเบา under way (water, glide, leap). */
  readonly qinggong: QinggongState;
  qgCache: { key: string; stats: QinggongStats } | null;
  /** Equipment bonuses beyond plain stats: lifesteal and per-art damage. */
  gear: { lifesteal: number; arts: Record<string, number> };
  /** Already setting off for another zone (once is enough). */
  travelling: boolean;
}

export function newPlayerSlot(entity: Entity, progress: PlayerProgress): PlayerSlot {
  return {
    entity,
    progress,
    input: { dx: 0, dy: 0, block: false, fly: 0, sprint: false, engage: false, talkTo: null, repathT: 0 },
    auto: { on: false, ax: 0, ay: 0, thinkT: 0 },
    qinggong: newQinggongState(0, 0),
    qgCache: null,
    gear: { lifesteal: 0, arts: {} },
    travelling: false,
  };
}

/** Who an event is for (docs/ONLINE.md §2). */
export type Audience = { zone: true } | { player: number } | { near: Point; r: number };

export interface Addressed {
  to: Audience;
  ev: WorldEvent;
}

/** How far a spatial event carries (tiles), when it has a place. */
export const NEAR_RADIUS = 48;

/**
 * Events only the player they concern sees: the UI's own (notices, logs, dialogs, rewards …).
 * Every other type is spatial (seen near where it happens) or, without a place, zone-wide.
 */
const PERSONAL = new Set<string>([
  'notice',
  'log',
  'dialog',
  'quest',
  'reward',
  'loot',
  'trained',
  'progressChanged',
  'travel',
  'jail',
  'party',
  'auto',
  'equipped',
  'learnedArt',
  'joinedSchool',
  'ladder',
]);

export function isPersonal(type: string): boolean {
  return PERSONAL.has(type);
}

/** Does `a` reach a player standing at `p` with id `id`? */
export function reaches(a: Audience, id: number, p: Point): boolean {
  if ('zone' in a) return true;
  if ('player' in a) return a.player === id;
  return Math.abs(p.x - a.near.x) <= a.r && Math.abs(p.y - a.near.y) <= a.r;
}
